import "./RemainingPagesResponsive.css";
import { useState, useEffect } from "react";
import api, { assetUrl } from "../api";
import { isImageFile, tooBig, MAX_FILE_MB, uploadErrorMessage } from "../uploadUtils";
import AdminLayout from "../components/AdminLayout";
import { getUser, isAdmin, ROLE_LABEL } from "../auth";
import {
  User, Bell, Building2, Users, MessageSquare, FolderClosed, LayoutTemplate,
  ShieldCheck, Blocks, Pencil, Upload, Check, X, Loader2, UserPlus, KeyRound, Phone, Mail, MoreVertical,
} from "lucide-react";


const SECTIONS = [
  { key: "profile", label: "Profile", icon: User },
  { key: "company", label: "Company", icon: Building2, adminOnly: true },
  { key: "team", label: "Team", icon: Users, adminOnly: true },
];

const LANGUAGES = ["English", "Hindi", "Punjabi", "Mandarin"];
const TIMEZONES = [
  "(GMT+10:00) Melbourne", "(GMT+10:00) Sydney", "(GMT+10:00) Brisbane",
  "(GMT+09:30) Adelaide", "(GMT+08:00) Perth", "(GMT+05:30) New Delhi",
];
const DATE_FORMATS = ["DD MMM YYYY (22 May 2024)", "DD/MM/YYYY (22/05/2024)", "MM/DD/YYYY (05/22/2024)", "YYYY-MM-DD (2024-05-22)"];
const PER_PAGE = [10, 25, 50, 100];
const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

/* ---------- shared bits ---------- */

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-gray-100 shadow-sm ${className}`}>{children}</div>
);

const Label = ({ children }) => (
  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
    {children}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 ${props.className || ""}`}
  />
);

const Select = ({ options, ...props }) => (
  <select
    {...props}
    className={`w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-slate-800 bg-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 ${props.className || ""}`}
  >
    {options.map((o) => (
      <option key={o} value={o}>{o}</option>
    ))}
  </select>
);

const Field = ({ label, children }) => (
  <div>
    <Label>{label}</Label>
    {children}
  </div>
);

const SectionHead = ({ title, action }) => (
  <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
    <h2 className="text-lg font-bold text-slate-900">{title}</h2>
    {action}
  </div>
);

const Flash = ({ flash, onClose }) => {
  if (!flash) return null;
  const ok = flash.type === "ok";
  return (
    <div className={`flex items-center gap-2 rounded-lg px-4 py-2.5 mb-4 text-sm font-medium ${ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
      {ok ? <Check size={15} /> : <X size={15} />}
      <span className="flex-1">{flash.text}</span>
      <button onClick={onClose} className="opacity-60 hover:opacity-100"><X size={14} /></button>
    </div>
  );
};

const SaveButton = ({ saving, children = "Save changes", ...props }) => (
  <button
    {...props}
    disabled={saving || props.disabled}
    className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white bg-teal-800 hover:bg-teal-900 disabled:opacity-60"
  >
    {saving && <Loader2 size={15} className="animate-spin" />}
    {saving ? "Saving…" : children}
  </button>
);

/* ---------- page ---------- */

export default function Settings() {
  const [active, setActive] = useState("profile");
  const admin = isAdmin();
  const sections = SECTIONS.filter((s) => admin || !s.adminOnly);

  return (
    <AdminLayout>
      <main className="p-3 sm:p-6 agp-rem-page">
        <Card className="px-6 py-5 mb-5">
          <h1 className="text-2xl font-extrabold text-slate-900">Settings</h1>
          <p className="text-sm text-slate-400 mt-0.5">Manage your account and application preferences.</p>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5 items-start">
          {/* side nav */}
          <Card className="p-2 overflow-hidden lg:sticky lg:top-6">
            <nav className="agp-rem-settings-nav">
              {sections.map((s) => {
                const on = active === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => setActive(s.key)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded-lg border-l-[3px] transition-colors ${
                      on
                        ? "border-blue-600 bg-blue-50/70 text-blue-700 font-semibold"
                        : "border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                    }`}
                  >
                    <s.icon size={17} className={on ? "text-blue-600" : "text-slate-400"} />
                    {s.label}
                  </button>
                );
              })}
            </nav>
          </Card>

          {/* panel */}
          <div className="space-y-5">
            {active === "profile" && <ProfileSection />}
            {active === "company" && admin && <CompanySection />}
            {active === "team" && admin && <TeamSection />}
            {!["profile", "company", "team"].includes(active) && (
              <ComingSoon section={SECTIONS.find((s) => s.key === active)} />
            )}
          </div>
        </div>
      </main>
    </AdminLayout>
  );
}

/* ---------- Profile ---------- */

function ProfileSection() {
  const [user, setUser] = useState(null);
  const [prefs, setPrefs] = useState({
    language: "English",
    timezone: "(GMT+10:00) Melbourne",
    dateFormat: "DD MMM YYYY (22 May 2024)",
    itemsPerPage: 25,
  });
  const [form, setForm] = useState({ name: "", jobTitle: "", email: "", mobile: "" });
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [flash, setFlash] = useState(null);

  const [pw, setPw] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings/profile");
      setUser(data.user);
      setForm({
        name: data.user.name || "",
        jobTitle: data.user.jobTitle || "",
        email: data.user.email || "",
        mobile: data.user.mobile || "",
      });
      if (data.user.preferences) setPrefs((p) => ({ ...p, ...data.user.preferences }));
    } catch {
      setFlash({ type: "err", text: "Couldn't load your profile." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings/profile", form);
      setUser(data.user);
      setEditing(false);
      setFlash({ type: "ok", text: "Profile updated." });
    } catch (err) {
      setFlash({ type: "err", text: err?.response?.data?.message || "Couldn't save your profile." });
    } finally {
      setSaving(false);
    }
  };

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.patch("/admin/settings/profile", { preferences: prefs });
      setFlash({ type: "ok", text: "Preferences saved." });
    } catch (err) {
      setFlash({ type: "err", text: err?.response?.data?.message || "Couldn't save preferences." });
    } finally {
      setSavingPrefs(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.newPassword !== pw.confirm) {
      setFlash({ type: "err", text: "New password and confirmation don't match." });
      return;
    }
    setSavingPw(true);
    try {
      await api.patch("/admin/settings/password", {
        currentPassword: pw.currentPassword,
        newPassword: pw.newPassword,
      });
      setPw({ currentPassword: "", newPassword: "", confirm: "" });
      setFlash({ type: "ok", text: "Password updated." });
    } catch (err) {
      setFlash({ type: "err", text: err?.response?.data?.message || "Couldn't update password." });
    } finally {
      setSavingPw(false);
    }
  };

  if (loading) {
    return <Card className="py-20 text-center text-slate-400">Loading profile…</Card>;
  }

  return (
    <>
      <div><Flash flash={flash} onClose={() => setFlash(null)} /></div>

      <Card>
        <SectionHead
          title="Profile Information"
          action={
            editing ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setEditing(false); setForm({ name: user.name || "", jobTitle: user.jobTitle || "", email: user.email || "", mobile: user.mobile || "" }); }}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <SaveButton saving={saving} onClick={saveProfile} />
              </div>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Pencil size={14} /> Edit Profile
              </button>
            )
          }
        />

        <div className="p-6">
          {editing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full name">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Job title">
                <Input value={form.jobTitle} placeholder="Sales Person" onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
              </Field>
              <Field label="Email">
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label="Mobile">
                <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
              </Field>
            </div>
          ) : (
            <div className="flex items-start gap-6">
              <span className="grid place-items-center w-16 h-16 rounded-full bg-[#14123a] text-white text-lg font-bold shrink-0">
                {initials(user?.name)}
              </span>
              <div className="min-w-0">
                <div className="text-xl font-bold text-slate-900">{user?.name}</div>
                <div className="text-sm text-slate-400 mt-0.5">{user?.jobTitle || ROLE_LABEL[user?.role] || "Staff"}</div>
                <div className="text-sm text-slate-500 mt-1.5">{user?.email}</div>
                <div className="text-sm text-slate-500">{user?.mobile || "No mobile on file"}</div>
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <SectionHead title="Preferences" action={<SaveButton saving={savingPrefs} onClick={savePrefs} children="Save preferences" />} />
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field label="Language">
            <Select options={LANGUAGES} value={prefs.language} onChange={(e) => setPrefs({ ...prefs, language: e.target.value })} />
          </Field>
          <Field label="Time zone">
            <Select options={TIMEZONES} value={prefs.timezone} onChange={(e) => setPrefs({ ...prefs, timezone: e.target.value })} />
          </Field>
          <Field label="Date format">
            <Select options={DATE_FORMATS} value={prefs.dateFormat} onChange={(e) => setPrefs({ ...prefs, dateFormat: e.target.value })} />
          </Field>
          <Field label="Items per page">
            <Select
              options={PER_PAGE.map(String)}
              value={String(prefs.itemsPerPage)}
              onChange={(e) => setPrefs({ ...prefs, itemsPerPage: Number(e.target.value) })}
            />
          </Field>

        </div>
      </Card>

      <Card>
        <SectionHead title="Change Password" />
        <form onSubmit={savePassword} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field label="Current password">
              <Input type="password" required autoComplete="current-password" value={pw.currentPassword}
                onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} />
            </Field>
            <Field label="New password">
              <Input type="password" required minLength={6} autoComplete="new-password" value={pw.newPassword}
                onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} />
            </Field>
            <Field label="Confirm new password">
              <Input type="password" required autoComplete="new-password" value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
            </Field>
          </div>
          <p className="text-xs text-slate-400 mt-3">Use at least 6 characters. You'll stay signed in on this device.</p>
          <div className="mt-5">
            <SaveButton saving={savingPw} type="submit" children="Update password" />
          </div>
        </form>
      </Card>
    </>
  );
}

/* ---------- Company ---------- */

const EMPTY_COMPANY = {
  legalName: "", tradingName: "", abn: "", acn: "", email: "", phone: "", website: "",
  addressLine1: "", addressLine2: "", suburb: "", state: "VIC", postcode: "", country: "Australia",
  logo: "", gstRate: 0, quotePrefix: "Q", defaultWarranty: "", defaultDelivery: "",
  bankName: "", accountName: "", bsb: "", accountNumber: "", quoteTerms: "",
};

function CompanySection() {
  const [form, setForm] = useState(EMPTY_COMPANY);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/company");
        setForm({ ...EMPTY_COMPANY, ...data, gstRate: 0 });
      } catch {
        setFlash({ type: "err", text: "Couldn't load company details." });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const pickLogo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!isImageFile(file)) { setFlash({ type: "err", text: "Please choose an image file (PNG, JPG or SVG)." }); return; }
    if (tooBig(file)) { setFlash({ type: "err", text: `Logo must be ${MAX_FILE_MB} MB or smaller.` }); return; }
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k !== "logo") fd.append(k, v ?? "");
      });
      if (logoFile) fd.append("logo", logoFile);

      const { data } = await api.patch("/admin/settings/company", fd);
      setForm({ ...EMPTY_COMPANY, ...data, gstRate: 0 });
      setLogoFile(null);
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      setLogoPreview("");
      setFlash({ type: "ok", text: "Company details saved." });
    } catch (err) {
      setFlash({ type: "err", text: uploadErrorMessage(err, "Couldn't save company details.") });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Card className="py-20 text-center text-slate-400">Loading company details…</Card>;
  }

  const shownLogo = logoPreview || (form.logo ? assetUrl(form.logo) : "");

  return (
    <>
      <div><Flash flash={flash} onClose={() => setFlash(null)} /></div>

      <Card>
        <SectionHead title="Company Details" action={<SaveButton saving={saving} onClick={save} />} />

        <div className="p-6 space-y-6">
          {/* logo */}
          <div className="flex items-center gap-5">
            <div className="grid place-items-center w-20 h-20 rounded-xl border border-dashed border-gray-200 bg-slate-50 overflow-hidden shrink-0">
              {shownLogo
                ? <img src={shownLogo} alt="Company logo" className="w-full h-full object-contain" />
                : <Building2 size={22} className="text-slate-300" />}
            </div>
            <div>
              <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer">
                <Upload size={14} /> Upload logo
                <input type="file" accept="image/*" onChange={pickLogo} className="hidden" />
              </label>
              <p className="text-xs text-slate-400 mt-2">PNG or SVG, transparent background, max 25MB. Appears on quotes.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Legal name"><Input value={form.legalName} onChange={set("legalName")} placeholder="All Good Parts Pty Ltd" /></Field>
            <Field label="Trading name"><Input value={form.tradingName} onChange={set("tradingName")} placeholder="AllGoodParts" /></Field>
            <Field label="ABN"><Input value={form.abn} onChange={set("abn")} placeholder="12 345 678 901" /></Field>
            <Field label="ACN"><Input value={form.acn} onChange={set("acn")} /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={set("email")} placeholder="sales@allgoodparts.com.au" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={set("phone")} placeholder="03 9000 0000" /></Field>
            <Field label="Website"><Input value={form.website} onChange={set("website")} placeholder="https://allgoodparts.com.au" /></Field>
          </div>
        </div>
      </Card>

      <Card>
        <SectionHead title="Business Address" />
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <Field label="Street address"><Input value={form.addressLine1} onChange={set("addressLine1")} placeholder="12 Industrial Drive" /></Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Address line 2"><Input value={form.addressLine2} onChange={set("addressLine2")} placeholder="Unit, level, warehouse" /></Field>
          </div>
          <Field label="Suburb"><Input value={form.suburb} onChange={set("suburb")} /></Field>
          <Field label="State"><Select options={AU_STATES} value={form.state} onChange={set("state")} /></Field>
          <Field label="Postcode"><Input value={form.postcode} onChange={set("postcode")} /></Field>
          <Field label="Country"><Input value={form.country} onChange={set("country")} /></Field>
        </div>
      </Card>

      <Card>
        <SectionHead title="Quote Defaults" />
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">

          <Field label="Quote ID prefix"><Input value={form.quotePrefix} onChange={set("quotePrefix")} placeholder="Q" /></Field>
          <Field label="Default warranty"><Input value={form.defaultWarranty} onChange={set("defaultWarranty")} placeholder="90 days parts warranty" /></Field>
          <Field label="Default delivery"><Input value={form.defaultDelivery} onChange={set("defaultDelivery")} placeholder="Road freight, 3–5 business days" /></Field>
          <div className="md:col-span-2">
            <Field label="Quote terms & conditions">
              <textarea
                rows={4}
                value={form.quoteTerms}
                onChange={set("quoteTerms")}
                placeholder="Shown at the bottom of every quote sent to a customer."
                className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              />
            </Field>
          </div>
        </div>
      </Card>

      <Card>
        <SectionHead title="Payment Details" action={<SaveButton saving={saving} onClick={save} />} />
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Bank name"><Input value={form.bankName} onChange={set("bankName")} /></Field>
          <Field label="Account name"><Input value={form.accountName} onChange={set("accountName")} /></Field>
          <Field label="BSB"><Input value={form.bsb} onChange={set("bsb")} placeholder="083-004" /></Field>
          <Field label="Account number"><Input value={form.accountNumber} onChange={set("accountNumber")} /></Field>
          <p className="md:col-span-2 text-xs text-slate-400">
            These appear on approved quotes and invoices. Only admins can see this section.
          </p>
        </div>
      </Card>
    </>
  );
}

/* ---------- placeholder for the sections still to come ---------- */

function ComingSoon({ section }) {
  if (!section) return null;
  const Icon = section.icon;
  return (
    <Card className="py-20 text-center">
      <span className="grid place-items-center w-12 h-12 rounded-xl bg-slate-50 text-slate-300 mx-auto">
        <Icon size={22} />
      </span>
      <h2 className="text-lg font-bold text-slate-800 mt-4">{section.label}</h2>
      <p className="text-sm text-slate-400 mt-1 max-w-sm mx-auto">
        Nothing here yet. This section gets built once the requirements are settled.
      </p>
    </Card>
  );
}

/* ---------- Team (admin only) ---------- */

export const DEFAULT_STAFF_PASSWORD = "1234";
const EMPTY_MEMBER = { name: "", email: "", mobile: "", password: DEFAULT_STAFF_PASSWORD, role: "worker" };

const StaffIdentity = ({ member, self }) => (
  <div className="flex items-center gap-3 min-w-0">
    <div className="h-10 w-10 shrink-0 rounded-full bg-indigo-100 text-indigo-700 font-bold text-sm flex items-center justify-center">{initials(member.name)}</div>
    <div className="min-w-0">
      <div className="font-semibold text-slate-900 text-sm break-words">{member.name}{self && <span className="text-xs font-normal text-slate-400"> (you)</span>}</div>
      {member.isActive === false && <div className="text-xs text-red-600 font-semibold">Disabled</div>}
    </div>
  </div>
);

const StaffRole = ({ member, self, busy, update }) => (
  <select value={member.role} disabled={self || busy} aria-label={`Role for ${member.name}`}
    onChange={(e) => update(member, { role: e.target.value }, `${member.name} is now ${ROLE_LABEL[e.target.value].toLowerCase()}.`)}
    className="max-w-full border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white disabled:bg-slate-50">
    <option value="worker">Worker</option><option value="admin">Admin</option>
  </select>
);

function StaffActions({ member, self, busy, resetFor, setResetFor, newPassword, setNewPassword, savePassword, update }) {
  const [open, setOpen] = useState(false);
  if (resetFor === member._id) return (
    <div className="flex flex-wrap items-center gap-2">
      <input aria-label={`New password for ${member.name}`} type="password" value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)} className="w-32 min-w-0 rounded-md border border-slate-200 p-2 text-sm" />
      <button type="button" disabled={busy} onClick={() => savePassword(member)} className="text-sm font-semibold text-teal-700">Save</button>
      <button type="button" onClick={() => { setResetFor(null); setNewPassword(""); }} className="text-sm text-slate-500">Cancel</button>
    </div>
  );
  return (
    <div className="relative">
      <button type="button" disabled={busy} aria-label={`Actions for ${member.name}`} aria-expanded={open}
        onClick={() => setOpen(!open)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-blue-700 font-medium inline-flex items-center gap-2">
        Manage <MoreVertical size={15}/>
      </button>
      {open && <div className="absolute right-0 top-full mt-1 z-20 w-44 rounded-lg border border-slate-200 bg-white shadow-lg p-1">
        <button type="button" onClick={() => { setResetFor(member._id); setNewPassword(""); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm rounded hover:bg-slate-50 flex gap-2 items-center"><KeyRound size={14}/> Reset password</button>
        {!self && <button type="button" onClick={() => {
          setOpen(false);
          if (member.isActive !== false && !window.confirm(`Disable ${member.name}? They'll be logged out and can't log in. Their quotes and replies stay on record.`)) return;
          update(member, { isActive: member.isActive === false }, member.isActive === false ? `${member.name} can log in again.` : `${member.name} has been disabled.`);
        }} className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-slate-50 ${member.isActive === false ? "text-green-700" : "text-red-600"}`}>
          {member.isActive === false ? "Enable account" : "Disable account"}
        </button>}
      </div>}
    </div>
  );
}

export function TeamSection({ standalone = false }) {
  const me = getUser();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/staff");
      setStaff(Array.isArray(data) ? data : []);
    } catch (err) {
      setFlash({ type: "err", text: err?.response?.data?.message || "Couldn't load your team." });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/staff", form);
      setFlash({ type: "ok", text: `${form.name} can now log in with ${form.email} and password ${form.password}. Ask them to change it in Settings > Profile.` });
      setForm(EMPTY_MEMBER);
      setAdding(false);
      load();
    } catch (err) {
      setFlash({ type: "err", text: err?.response?.data?.message || "Couldn't add this person." });
    } finally {
      setSaving(false);
    }
  };

  const update = async (m, patch, okText) => {
    setBusyId(m._id);
    try {
      await api.patch(`/admin/staff/${m._id}`, patch);
      setFlash({ type: "ok", text: okText });
      load();
    } catch (err) {
      setFlash({ type: "err", text: err?.response?.data?.message || "Couldn't save that change." });
    } finally {
      setBusyId(null);
    }
  };

  const savePassword = async (m) => {
    if (newPassword.length < 4) return setFlash({ type: "err", text: "Password must be at least 4 characters." });
    await update(m, { password: newPassword }, `Password for ${m.name} is now ${newPassword}.`);
    setResetFor(null);
    setNewPassword("");
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—");

  return (
    <Card>
      <SectionHead
        title={standalone ? "Workers & admins" : "Team"}
        action={!adding && (
          <button onClick={() => setAdding(true)}
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white bg-slate-800 hover:bg-slate-900">
            <UserPlus size={15} /> Add worker
          </button>
        )}
      />
      <div className="p-6">
        <Flash flash={flash} onClose={() => setFlash(null)} />
        <p className="text-sm text-slate-500 mb-5">
          <b>Admins</b> have full access. <b>Workers</b> can handle requests, send quotes and reply to customers,
          but can't manage customers, company settings or the team. Everything each person sends is recorded on the request.
        </p>

        {adding && (
          <form onSubmit={add} className="rounded-xl border border-gray-200 p-5 mb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Email (used to log in)"><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Mobile"><Input type="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></Field>
              <Field label="Password">
                <Input required minLength={4} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <p className="text-xs text-slate-400 mt-1">Default is {DEFAULT_STAFF_PASSWORD}. They can change it in Settings &gt; Profile.</p>
              </Field>
            </div>
            <div>
              <Label>Role</Label>
              <div className="inline-flex rounded-lg bg-slate-100 p-1" role="group" aria-label="Role">
                {["worker", "admin"].map((r) => (
                  <button type="button" key={r} onClick={() => setForm({ ...form, role: r })} aria-pressed={form.role === r}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold ${form.role === r ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
                    {ROLE_LABEL[r]}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <SaveButton saving={saving} type="submit">Add {ROLE_LABEL[form.role].toLowerCase()}</SaveButton>
              <button type="button" onClick={() => { setAdding(false); setForm(EMPTY_MEMBER); }}
                className="rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : staff.length === 0 ? (
          <p className="text-sm text-slate-500">No team members yet.</p>
        ) : (
          <>
            {/* Keep actions shared between desktop rows and mobile cards. */}
            <div className="hidden lg:block overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[940px] text-left border-collapse">
                <thead className="bg-[#111134] text-white text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-5 py-5 font-semibold">Worker</th>
                    <th className="px-4 py-5 font-semibold">Contact</th>
                    <th className="px-4 py-5 font-semibold">Email</th>
                    <th className="px-4 py-5 font-semibold">Role</th>
                    <th className="px-4 py-5 text-center font-semibold">Quotes sent</th>
                    <th className="px-4 py-5 text-center font-semibold">Replies</th>
                    <th className="px-4 py-5 font-semibold">Last activity</th>
                    <th className="px-4 py-5 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staff.map((m) => (
                    <tr key={m._id} className={`${m.isActive === false ? "opacity-60" : ""} hover:bg-slate-50/60`}>
                      <td className="px-5 py-5"><StaffIdentity member={m} self={String(m._id) === String(me?.id)} /></td>
                      <td className="px-4 py-5 whitespace-nowrap text-sm text-slate-600">{m.mobile ? <a href={`tel:${m.mobile}`} className="inline-flex gap-2 items-center"><Phone size={14} className="text-slate-400" />{m.mobile}</a> : "—"}</td>
                      <td className="px-4 py-5 max-w-[250px] break-all text-sm text-slate-600">{m.email ? <a href={`mailto:${m.email}`} className="inline-flex gap-2 items-center"><Mail size={14} className="shrink-0 text-slate-400" />{m.email}</a> : "—"}</td>
                      <td className="px-4 py-5"><StaffRole member={m} self={String(m._id) === String(me?.id)} busy={busyId === m._id} update={update}/></td>
                      <td className="px-4 py-5 text-center font-semibold text-slate-900">{m.quotesSent ?? 0}</td>
                      <td className="px-4 py-5 text-center font-semibold text-slate-900">{m.replies ?? 0}</td>
                      <td className="px-4 py-5 text-sm text-slate-700 whitespace-nowrap">{fmt(m.lastActive)}</td>
                      <td className="px-4 py-5"><StaffActions member={m} self={String(m._id) === String(me?.id)} busy={busyId === m._id} resetFor={resetFor} setResetFor={setResetFor} newPassword={newPassword} setNewPassword={setNewPassword} savePassword={savePassword} update={update} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-3">
              {staff.map((m) => (
                <article key={m._id} className={`rounded-xl border border-slate-200 bg-white p-4 shadow-sm min-w-0 ${m.isActive === false ? "opacity-60" : ""}`}>
                  <StaffIdentity member={m} self={String(m._id) === String(me?.id)} />
                  <div className="mt-3 space-y-1 text-xs text-slate-500 break-all">
                    <div>{m.email}</div><div>{m.mobile || "No phone number"}</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 border-t border-slate-100 mt-3 pt-3 mb-3 text-xs">
                    <div><span className="block text-slate-400">Quotes sent</span><strong className="text-slate-900">{m.quotesSent ?? 0}</strong></div>
                    <div><span className="block text-slate-400">Replies</span><strong className="text-slate-900">{m.replies ?? 0}</strong></div>
                    <div><span className="block text-slate-400">Last activity</span><strong className="text-slate-900">{fmt(m.lastActive)}</strong></div>
                  </div>
                  <div className="flex flex-wrap gap-3 items-center justify-between border-t border-slate-100 pt-3">
                    <StaffRole member={m} self={String(m._id) === String(me?.id)} busy={busyId === m._id} update={update}/>
                    <StaffActions member={m} self={String(m._id) === String(me?.id)} busy={busyId === m._id} resetFor={resetFor} setResetFor={setResetFor} newPassword={newPassword} setNewPassword={setNewPassword} savePassword={savePassword} update={update}/>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
